import { useState } from "react";
import { Eye, EyeOff, Loader2, Lock, User, ArrowRight, ShieldCheck, NotebookPen, Wallet, Users, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const BENEFITS = [
  { icon: Wallet, text: "Uang Kas dan Uang Kas Setelah Kasbon dihitung otomatis." },
  { icon: Users, text: "Kasbon pelanggan dan kasbon pabrik tercatat rapi." },
  { icon: Package, text: "Stok bahan baku terpantau, peringatan saat menipis." },
];

function BrandPanel() {
  return (
    <section className="login-panel relative w-full md:w-[48%] lg:w-[55%] text-white" data-testid="login-brand-panel"
      style={{ backgroundImage: "linear-gradient(180deg, rgba(8, 46, 27, 0.78), rgba(8, 46, 27, 0.9)), url('/login-bg.jpg')" }}>
      <div className="relative px-6 sm:px-10 lg:px-16 xl:px-24 py-10 sm:py-12 md:py-16 md:min-h-screen flex flex-col justify-center">
        <img src="/logo-640.png" alt="Logo Juragan Tahu" data-testid="login-logo"
          className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-48 lg:h-48 object-contain rounded-[22%] shadow-2xl jt-fade" />
        <h1 className="mt-7 md:mt-10 font-extrabold leading-[1.05] tracking-tight text-[2rem] sm:text-4xl lg:text-5xl xl:text-6xl jt-fade" style={{ animationDelay: "80ms" }}>
          Jualan Makin Tenang,<br />
          <span className="text-[#e3c272]">Keuntungan Lebih Jelas.</span>
        </h1>
        <div className="mt-6 md:mt-8 flex gap-3 max-w-xl jt-fade" style={{ animationDelay: "160ms" }}>
          <NotebookPen className="w-6 h-6 shrink-0 text-[#e3c272] mt-0.5" />
          <p className="text-[15px] sm:text-base lg:text-lg text-white/85 leading-relaxed" data-testid="login-description">
            Catat pemasukan, pengeluaran, kasbon, dan stok bahan baku dalam satu tempat. Aplikasi yang menghitung, Anda tinggal mencatat.
          </p>
        </div>
        <ul className="mt-6 md:mt-8 space-y-3 max-w-xl jt-fade" style={{ animationDelay: "240ms" }}>
          {BENEFITS.map(({ icon: I, text }) => (
            <li key={text} className="flex items-center gap-3 rounded-xl bg-white/[0.07] border border-white/10 backdrop-blur-sm px-4 py-3">
              <span className="w-8 h-8 rounded-lg bg-[#e3c272]/15 flex items-center justify-center shrink-0"><I className="w-4 h-4 text-[#e3c272]" /></span>
              <span className="text-sm sm:text-[15px] text-white/90">{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function LoginForm() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) { setError("Isi nama akun dan kata sandi terlebih dahulu."); return; }
    setLoading(true); setError("");
    try { await login(username, password); }
    catch (err) { setError(errMsg(err, "Gagal masuk. Silakan coba lagi.")); setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="jt-card w-full max-w-md p-6 sm:p-9 jt-fade" data-testid="login-form">
      <div className="w-12 h-1 rounded-full bg-[var(--jt-gold)] mb-6" />
      <h2 className="text-2xl sm:text-3xl font-extrabold text-[var(--jt-emerald)]">Selamat Datang, Juragan!</h2>
      <p className="mt-1.5 text-sm sm:text-base text-muted-foreground">Masuk untuk melihat catatan usaha Anda.</p>
      <label className="block mt-7 text-sm font-bold" htmlFor="username">Nama Akun</label>
      <div className="relative mt-2">
        <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input id="username" className="jt-input pl-10 min-h-[3rem]" placeholder="Contoh: pabrik tahu10" autoComplete="username"
          autoCapitalize="none" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="login-username-input" />
      </div>
      <label className="block mt-5 text-sm font-bold" htmlFor="password">Kata Sandi</label>
      <div className="relative mt-2">
        <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input id="password" type={show ? "text" : "password"} className="jt-input pl-10 pr-12 min-h-[3rem]" placeholder="Masukkan kata sandi"
          autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-black/5"
          data-testid="login-password-toggle">
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
      {error && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert" data-testid="login-error">{error}</p>}
      <button type="submit" disabled={loading} className="jt-btn jt-btn-primary w-full mt-6 min-h-[3.1rem] text-base" data-testid="login-submit-button">
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Memeriksa...</> : <>Masuk <ArrowRight className="w-5 h-5" /></>}
      </button>
      <div className="mt-6 flex gap-3 items-start rounded-xl bg-[#eef6ee] border border-[#d5e8d6] px-4 py-3">
        <ShieldCheck className="w-5 h-5 text-[var(--jt-green)] shrink-0 mt-0.5" />
        <p className="text-sm text-[#2c4a36]" data-testid="login-security-note">Akses hanya bisa digunakan oleh akun Juragan tahu yang sudah membeli ya.</p>
      </div>
    </form>
  );
}

export default function Login() {
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[var(--jt-cream)] overflow-x-hidden" data-testid="login-page">
      <BrandPanel />
      <section className="flex-1 flex items-center justify-center px-4 sm:px-8 py-8 md:py-12 bg-[var(--jt-cream)]">
        <LoginForm />
      </section>
    </div>
  );
}
